import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import Button from './Button';

/**
 * Text with inline links, for paragraphs, bullet items and quotes.
 *
 *   [Homeland Stay](https://homelandstay.com)           plain text link
 *   [Book your stay](/rooms){button}                    same link, shown as a button
 *
 * Anything else is plain text. Only http(s), mailto, tel, root-relative and
 * in-page (#) targets are linked, so a pasted "javascript:" URL stays text.
 *
 * `tone="light"` swaps the plain-text-link color for one readable on a dark
 * background (a hero slide over a photo) — the default olive is a dark
 * green, invisible against that. The {button} form doesn't need this: it's
 * already a solid, self-contained pill either way.
 */
const LINK_PATTERN = /\[([^\]]+)\]\(([^)\s]+)\)(\{button\})?/g;
const SAFE_HREF = /^(https?:\/\/|mailto:|tel:|\/|#)/i;

/**
 * For a field that's reused somewhere a real link can't safely go — e.g. a
 * blog excerpt shown both on the post's own header (safe) and inside a card
 * that's itself wrapped in a link to that post (nesting an <a> inside an
 * <a> is invalid HTML and breaks the click). Drops the [label](url){button}
 * syntax down to just its plain label, so that spot shows clean text
 * instead of the raw bracket syntax leaking through.
 */
export function stripInlineLinks(text: string): string {
  return text.replace(LINK_PATTERN, (whole, label, href) => (SAFE_HREF.test(href) ? label : whole));
}

export default function InlineText({ text, tone = 'default' }: { text: string; tone?: 'default' | 'light' }) {
  const linkClass =
    tone === 'light'
      ? 'text-gold-light underline underline-offset-2 hover:text-paper'
      : 'text-olive-dark underline underline-offset-2 hover:text-olive';
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  LINK_PATTERN.lastIndex = 0;

  while ((match = LINK_PATTERN.exec(text)) !== null) {
    const [whole, label, href, asButton] = match;
    if (match.index > last) nodes.push(text.slice(last, match.index));
    last = match.index + whole.length;

    if (!SAFE_HREF.test(href)) {
      nodes.push(whole);
      continue;
    }

    const key = `${match.index}-${href}`;
    const external = /^https?:\/\//i.test(href);
    if (asButton) {
      nodes.push(
        <Fragment key={key}>
          {external ? (
            <Button href={href} size="md">
              {label}
            </Button>
          ) : (
            <Button to={href} size="md">
              {label}
            </Button>
          )}
        </Fragment>,
      );
    } else if (external) {
      nodes.push(
        <a key={key} href={href} target="_blank" rel="noopener noreferrer" className={linkClass}>
          {label}
        </a>,
      );
    } else {
      nodes.push(
        <Link key={key} to={href} className={linkClass}>
          {label}
        </Link>,
      );
    }
  }

  if (last < text.length) nodes.push(text.slice(last));
  return <>{nodes}</>;
}
